import type { JsonObject, JsonValue } from '../api/types';
import { deepEqual, isPlainObject } from './json';

/**
 * Builds the minimal RFC 7396 merge patch that turns `source` into `target`.
 * Objects are compared member by member; arrays and scalars are replaced whole;
 * members missing from `target` become `null`.
 */
export function createMergePatch(source: JsonObject, target: JsonObject): JsonObject {
  const patch: JsonObject = {};
  for (const [key, targetValue] of Object.entries(target)) {
    if (targetValue === undefined) continue;
    const sourceValue = source[key];
    if (!(key in source) || sourceValue === undefined) {
      patch[key] = targetValue;
      continue;
    }
    if (isPlainObject(sourceValue) && isPlainObject(targetValue)) {
      const inner = createMergePatch(sourceValue as JsonObject, targetValue as JsonObject);
      if (Object.keys(inner).length > 0) patch[key] = inner;
      continue;
    }
    if (!deepEqual(sourceValue, targetValue)) patch[key] = targetValue;
  }
  for (const key of Object.keys(source)) {
    if (source[key] !== undefined && (!(key in target) || target[key] === undefined)) patch[key] = null;
  }
  return patch;
}

/** Applies an RFC 7396 merge patch. */
export function applyMergePatch(target: JsonValue | undefined, patch: JsonValue): JsonValue {
  if (!isPlainObject(patch)) return patch;
  const result: JsonObject = isPlainObject(target) ? { ...(target as JsonObject) } : {};
  for (const [key, value] of Object.entries(patch as JsonObject)) {
    if (value === null) delete result[key];
    else result[key] = applyMergePatch(result[key], value);
  }
  return result;
}
